// Minimal ISO-BMFF / WAV helpers for building and inspecting replay clips.

const u32 = (b: Uint8Array, o: number) => ((b[o] << 24) | (b[o + 1] << 16) | (b[o + 2] << 8) | b[o + 3]) >>> 0;
const type4 = (b: Uint8Array, o: number) => String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]);

function* boxes(b: Uint8Array, start: number, end: number): Generator<{ type: string; start: number; end: number; body: number }> {
  let o = start;
  while (o + 8 <= end) {
    let size = u32(b, o);
    let header = 8;
    if (size === 1) {
      size = u32(b, o + 8) * 2 ** 32 + u32(b, o + 12);
      header = 16;
    } else if (size === 0) size = end - o;
    if (size < header || o + size > end) return;
    yield { type: type4(b, o + 4), start: o, end: o + size, body: o + header };
    o += size;
  }
}

/** Counts samples per track ID across every moof in `data` (a clip made of init + fragments). */
export function countSamples(data: Uint8Array): Map<number, number> {
  const counts = new Map<number, number>();
  for (const top of boxes(data, 0, data.length)) {
    if (top.type !== 'moof') continue;
    for (const traf of boxes(data, top.body, top.end)) {
      if (traf.type !== 'traf') continue;
      let trackId = 0;
      let samples = 0;
      for (const b of boxes(data, traf.body, traf.end)) {
        if (b.type === 'tfhd') trackId = u32(data, b.body + 4);
        if (b.type === 'trun') samples += u32(data, b.body + 4);
      }
      counts.set(trackId, (counts.get(trackId) ?? 0) + samples);
    }
  }
  return counts;
}

function timescaleOf(init: Uint8Array, trackId: number): number {
  for (const top of boxes(init, 0, init.length)) {
    if (top.type !== 'moov') continue;
    for (const trak of boxes(init, top.body, top.end)) {
      if (trak.type !== 'trak') continue;
      let id = 0;
      let ts = 0;
      for (const b of boxes(init, trak.body, trak.end)) {
        if (b.type === 'tkhd') id = u32(init, b.body + (init[b.body] === 1 ? 20 : 12));
        if (b.type === 'mdia')
          for (const m of boxes(init, b.body, b.end)) if (m.type === 'mdhd') ts = u32(init, m.body + (init[m.body] === 1 ? 20 : 12));
      }
      if (id === trackId) return ts;
    }
  }
  return 0;
}

/** End time (s) of a CFR video track's samples in one moof, given the file's init segment. */
export function fragmentEndTime(init: Uint8Array, moof: Uint8Array, fps: number, trackId = 1): number | null {
  const timescale = timescaleOf(init, trackId);
  if (!timescale) return null;
  for (const top of boxes(moof, 0, moof.length)) {
    if (top.type !== 'moof') continue;
    for (const traf of boxes(moof, top.body, top.end)) {
      if (traf.type !== 'traf') continue;
      let id = 0;
      let tfdt = 0;
      let samples = 0;
      for (const b of boxes(moof, traf.body, traf.end)) {
        if (b.type === 'tfhd') id = u32(moof, b.body + 4);
        if (b.type === 'tfdt') tfdt = moof[b.body] === 1 ? u32(moof, b.body + 4) * 2 ** 32 + u32(moof, b.body + 8) : u32(moof, b.body + 4);
        if (b.type === 'trun') samples += u32(moof, b.body + 4);
      }
      if (id === trackId) return tfdt / timescale + samples / fps;
    }
  }
  return null;
}

/**
 * Frame rate of a track in a fragmented clip, from its own decode timestamps:
 * samples before the last fragment ÷ time between the first and last fragment.
 */
export function trackFps(data: Uint8Array, trackId = 1): number | null {
  let timescale = 0;
  for (const top of boxes(data, 0, data.length)) {
    if (top.type !== 'moov') continue;
    for (const trak of boxes(data, top.body, top.end)) {
      if (trak.type !== 'trak') continue;
      let id = 0;
      let ts = 0;
      for (const b of boxes(data, trak.body, trak.end)) {
        if (b.type === 'tkhd') id = u32(data, b.body + (data[b.body] === 1 ? 20 : 12));
        if (b.type === 'mdia')
          for (const m of boxes(data, b.body, b.end)) if (m.type === 'mdhd') ts = u32(data, m.body + (data[m.body] === 1 ? 20 : 12));
      }
      if (id === trackId) timescale = ts;
    }
  }
  if (!timescale) return null;
  const frags: { tfdt: number; samples: number }[] = [];
  for (const top of boxes(data, 0, data.length)) {
    if (top.type !== 'moof') continue;
    for (const traf of boxes(data, top.body, top.end)) {
      if (traf.type !== 'traf') continue;
      let id = 0;
      let tfdt = 0;
      let samples = 0;
      for (const b of boxes(data, traf.body, traf.end)) {
        if (b.type === 'tfhd') id = u32(data, b.body + 4);
        if (b.type === 'tfdt') tfdt = data[b.body] === 1 ? u32(data, b.body + 4) * 2 ** 32 + u32(data, b.body + 8) : u32(data, b.body + 4);
        if (b.type === 'trun') samples += u32(data, b.body + 4);
      }
      if (id === trackId) frags.push({ tfdt, samples });
    }
  }
  if (frags.length < 2) return null;
  const span = (frags[frags.length - 1].tfdt - frags[0].tfdt) / timescale;
  const counted = frags.slice(0, -1).reduce((a, f) => a + f.samples, 0);
  return span > 0 ? Math.round((counted / span) * 10) / 10 : null;
}

/** A standalone 16/24-bit PCM WAV built from raw little-endian samples. */
export function wavFile(pcm: Uint8Array, sampleRate: number, channels: number, bitsPerSample: number): Uint8Array {
  const out = new Uint8Array(44 + pcm.length);
  const v = new DataView(out.buffer);
  const blockAlign = channels * (bitsPerSample / 8);
  out.set([0x52, 0x49, 0x46, 0x46], 0); // RIFF
  v.setUint32(4, 36 + pcm.length, true);
  out.set([0x57, 0x41, 0x56, 0x45, 0x66, 0x6d, 0x74, 0x20], 8); // WAVEfmt
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, channels, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * blockAlign, true);
  v.setUint16(32, blockAlign, true);
  v.setUint16(34, bitsPerSample, true);
  out.set([0x64, 0x61, 0x74, 0x61], 36); // data
  v.setUint32(40, pcm.length, true);
  out.set(pcm, 44);
  return out;
}

/** Peak and RMS level of PCM data in dBFS (mixes all channels). */
export function pcmLevels(pcm: Uint8Array, bitsPerSample: number): { peakDb: number; rmsDb: number } {
  const bytes = bitsPerSample / 8;
  const n = Math.floor(pcm.length / bytes);
  if (!n) return { peakDb: -Infinity, rmsDb: -Infinity };
  const full = 2 ** (bitsPerSample - 1);
  let peak = 0;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const o = i * bytes;
    let s: number;
    if (bytes === 2) s = ((pcm[o + 1] << 24) | (pcm[o] << 16)) >> 16;
    else s = ((pcm[o + 2] << 24) | (pcm[o + 1] << 16) | (pcm[o] << 8)) >> 8;
    const x = Math.abs(s) / full;
    if (x > peak) peak = x;
    sum += x * x;
  }
  const db = (x: number) => (x > 0 ? 20 * Math.log10(x) : -Infinity);
  return { peakDb: db(peak), rmsDb: db(Math.sqrt(sum / n)) };
}
